import ActionButton from "@able/react/dist/ActionButton";
import React, { MouseEventHandler, PropsWithChildren, Ref, useEffect, useLayoutEffect, useRef, useState } from "react";
import { classNames, useResize } from "../util/helpers";
import './style.scss';

export type DynamicAdjustableListProps = {
    allButtonLabel?: string;
    onClickAllButton?: MouseEventHandler | undefined,
    className?: string;
    allBtnRef?: Ref<HTMLButtonElement>;
} & PropsWithChildren;

const DynamicAdjustableList = (props: DynamicAdjustableListProps) => {

    const { children, className, onClickAllButton: onClick, allButtonLabel = "All items...", allBtnRef } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [visibleCount, setVisibleCount] = useState(React.Children.count(children));
    const [allBtnWidth, setAllBtnWidth] = useState(0);
    const childRefs = useRef<HTMLElement[]>([]);
    const [size, setSize] = useState('');
    const localRef = useRef<HTMLButtonElement>(null);

    // Sync the forwarded ref with the local ref
    useEffect(() => {
        if (!allBtnRef) return;

        if (typeof allBtnRef === 'function') {
            allBtnRef(localRef.current);
        } else {
            (allBtnRef as any).current = localRef.current;
        }
    }, [allBtnRef, localRef.current, size, containerWidth]);

    useLayoutEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.clientWidth);
        }
    }, []);

    useEffect(() => {
        if (localRef.current) {
            setAllBtnWidth(localRef.current.clientWidth);
        }
    }, [(localRef.current)]);

    useResize((size) => {
        const handleResize = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.clientWidth);
            }

            if (localRef.current) {
                setAllBtnWidth(localRef.current.clientWidth);
            }
        }

        if (size === 'xs') {
            setSize('xs')
        } else {
            handleResize();
            setSize('');
        }
    }, [containerRef]);

    useLayoutEffect(() => {

        const widths = childRefs.current.map((ref: HTMLElement) => (ref ? ref.clientWidth : 0));
        let total = 0;
        let count = 0;
        const gap = 8;
        const calculateGap = (w: number) => (w > 0 ? gap : 0)

        for (let i = 0; i < widths.length; i++) {
            let width = widths[i];
            const isLast = (widths.length - 1) === i;
            let cg = calculateGap(width);

            if (i === (visibleCount - 1)) {
                cg = 0;
            }

            width = width + (isLast ? 0 : cg);

            if (total + width + allBtnWidth <= containerWidth) {
                total += width;
                count++;
            } else {
                break;
            }
        }

        setVisibleCount(count);

    }, [containerWidth, allBtnWidth]);

    childRefs.current = [];

    const childrenArray = React.Children.toArray(children);

    return (<>
        <div className={classNames("ted-ui-DynamicAdjustableContainer", visibleCount === 0 && '--row-reverse')} ref={containerRef}>
            <div className={classNames("ted-ui-DynamicAdjustable", className)}>
                {childrenArray.slice(0, size === 'xs' ? 0 : visibleCount).map((child, index) => <div key={index} className="ted-ui-DynamicAdjustableItem">{child}</div>)}
                {(childrenArray.length > visibleCount || size === 'xs') &&
                    <ActionButton className={classNames('ted-ui-DynamicAdjustableBtn')} elementRef={localRef as any} label={allButtonLabel} events={{ onClick }} element={"button"} variant={"MediumEmphasisCompact"} />
                }
            </div>
        </div>
        {/** Hidden calculation */}
        <div className="ted-ui-DynamicAdjustableHidden" aria-hidden="true">
            {childrenArray.map((child, index) => {
                return <div key={index} ref={el => ((childRefs as any).current[index] = el)}>
                    {child}
                </div>
            })}
        </div>
    </>);
}

DynamicAdjustableList.displayName = 'DynamicAdjustableList';

export default DynamicAdjustableList;